import type { Ga4Integration, Ga4Property } from '@/api/types'
import type { BadgeTone } from '@/design'

/** A property still being imported; a disconnected one only keeps the traffic it already has. */
export function isLive(property: Ga4Property): boolean {
  return property.status !== 'disconnected'
}

export interface StatusLabel {
  label: string
  tone: BadgeTone
}

/** The connection: is Google still letting us read this property? */
export function connectionLabel(property: Ga4Property): StatusLabel {
  switch (property.status) {
    case 'connected':
      return { label: 'Connected', tone: 'positive' }
    case 'error':
      return { label: 'Access lost', tone: 'critical' }
    case 'disconnected':
      return { label: 'Disconnected', tone: 'neutral' }
  }
}

/** The data: how current the last import leaves the Traffic page. */
export function freshnessLabel(property: Ga4Property): StatusLabel | null {
  if (!isLive(property)) return null
  if (property.stale) return { label: 'Stale', tone: 'warning' }
  if (property.providerLag) return { label: 'Delayed', tone: 'warning' }
  if (property.lastImportStatus === 'running' || property.lastImportStatus === 'pending') {
    return { label: 'Importing', tone: 'info' }
  }
  return { label: 'Up to date', tone: 'positive' }
}

/** The Integrations list's one badge for the whole integration. */
export function integrationLabel(integration: Ga4Integration): StatusLabel {
  if (!integration.configured) return { label: 'Not configured', tone: 'neutral' }
  const live = integration.properties.filter(isLive)
  if (live.length === 0) return { label: 'Not connected', tone: 'neutral' }
  if (live.some((property) => property.status === 'error')) return { label: 'Needs attention', tone: 'warning' }
  if (live.some((property) => property.stale)) return { label: 'Stale', tone: 'warning' }
  return { label: 'Connected', tone: 'positive' }
}
